import axios from "axios";
import { loggedOut } from "../features/auth/authSlice";

// The API's envelope is documented as lowercase { success, message, data },
// but at least one error path (invalid login) has been observed returning
// PascalCase { Success, Message, Data } instead - read case-insensitively
// everywhere rather than trust either casing.
const readEnvelopeField = (payload, field) => {
  if (!payload || typeof payload !== "object") return undefined;
  const key = Object.keys(payload).find(
    (k) => k.toLowerCase() === field.toLowerCase()
  );
  return key ? payload[key] : undefined;
};

const isEnvelope = (payload) => readEnvelopeField(payload, "success") !== undefined;

/**
 * RTK Query baseQuery backed by axios, so we get axios ergonomics (interceptors,
 * FormData handling) together with RTK Query's caching/invalidation.
 * Unwraps the API's universal { success, message, data } envelope so every
 * endpoint definition can just deal with plain data. `responseType` passes
 * straight through to axios (e.g. "blob" for the customer QR code endpoint,
 * handoff §19.1, which returns raw image/png not JSON) - a Blob has no
 * enumerable "success" key, so isEnvelope() correctly falls through and
 * returns it as-is rather than trying to unwrap it as JSON.
 */
export const axiosBaseQuery =
  ({ baseUrl }) =>
  async ({ url, method = "GET", data, params, responseType }, { getState, dispatch }) => {
    const token = getState()?.auth?.token;
    try {
      const result = await axios({
        url: baseUrl + url,
        method,
        data,
        params,
        responseType,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const envelope = result.data;
      if (isEnvelope(envelope)) {
        if (!readEnvelopeField(envelope, "success")) {
          return {
            error: {
              status: result.status,
              message:
                readEnvelopeField(envelope, "message") || "Request failed.",
            },
          };
        }
        return { data: readEnvelopeField(envelope, "data") };
      }
      return { data: envelope };
    } catch (axiosError) {
      const status = axiosError.response?.status;
      const message =
        readEnvelopeField(axiosError.response?.data, "message") ||
        axiosError.message ||
        "Request failed.";

      // Expired/invalid token: drop it so ProtectedRoute reactively bounces
      // the user back to /signin instead of every screen having to check.
      if (status === 401 && token) {
        dispatch(loggedOut());
      }

      return { error: { status, message } };
    }
  };
